/**
 * CompassCheckModal.js
 *
 * 3-MODE compass check for WBC3 19.2.6 compliance.
 * Scales from small workboats to large tugs.
 *
 * Modes:
 * - quick:    2-tap OK/Error for small boats (<15m)
 * - standard: Heading + deviation value for medium boats (15-24m)
 * - full:     Complete STCW-level logging for large boats (24m+)
 *
 * Mode is set per-vessel in vessel settings.
 */

import React, { useState, useEffect } from 'react';
import { X, Compass, CheckCircle, AlertTriangle, ChevronLeft } from 'lucide-react';

// 8-point compass for heading selection (standard/full modes)
const HEADINGS = [
  { value: 0, label: 'N' },
  { value: 45, label: 'NE' },
  { value: 90, label: 'E' },
  { value: 135, label: 'SE' },
  { value: 180, label: 'S' },
  { value: 225, label: 'SW' },
  { value: 270, label: 'W' },
  { value: 315, label: 'NW' }
];

// Deviation ranges for quick mode
const DEVIATION_RANGES = [
  { value: '1-2', label: '1-2°', description: 'Minor' },
  { value: '3-5', label: '3-5°', description: 'Notable' },
  { value: '6-10', label: '6-10°', description: 'Significant' },
  { value: '10+', label: '10+°', description: 'Major' }
];

// Check methods for full mode
const CHECK_METHODS = [
  { value: 'gps', label: 'GPS Comparison' },
  { value: 'transit', label: 'Transit Bearing' },
  { value: 'celestial', label: 'Sun/Star Azimuth' },
  { value: 'known_bearing', label: 'Known Bearing' }
];

const CompassCheckModal = ({
  isOpen,
  onClose,
  onSubmit,
  mode = 'quick'  // 'quick' | 'standard' | 'full'
}) => {
  // Shared state
  const [step, setStep] = useState('initial');
  const [notes, setNotes] = useState('');

  // Quick mode state
  const [deviationRange, setDeviationRange] = useState('');

  // Standard mode state
  const [heading, setHeading] = useState(null);
  const [customHeading, setCustomHeading] = useState('');
  const [showCustomHeading, setShowCustomHeading] = useState(false);
  const [deviation, setDeviation] = useState('');
  const [deviationDirection, setDeviationDirection] = useState('E');

  // Full mode state
  const [method, setMethod] = useState('');
  const [gyroError, setGyroError] = useState('');
  const [gyroHeading, setGyroHeading] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('initial');
      setNotes('');
      setDeviationRange('');
      setHeading(null);
      setCustomHeading('');
      setShowCustomHeading(false);
      setDeviation('');
      setDeviationDirection('E');
      setMethod('');
      setGyroError('');
      setGyroHeading('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Get final heading value
  const getFinalHeading = () => {
    if (showCustomHeading) return parseInt(customHeading, 10);
    return heading;
  };

  // ============================================================================
  // QUICK MODE HANDLERS
  // ============================================================================

  const handleQuickOK = () => {
    onSubmit({
      mode: 'quick',
      status: 'ok',
      timestamp: new Date().toISOString()
    });
    onClose();
  };

  const handleQuickError = () => {
    setStep('quick_error_detail');
  };

  const handleQuickErrorSubmit = () => {
    if (!deviationRange) return;
    onSubmit({
      mode: 'quick',
      status: 'error',
      deviationRange,
      notes: notes.trim() || null,
      timestamp: new Date().toISOString()
    });
    onClose();
  };

  // ============================================================================
  // STANDARD MODE HANDLERS
  // ============================================================================

  const handleStandardSubmit = () => {
    const finalHeading = getFinalHeading();
    if (finalHeading === null || isNaN(finalHeading) || !deviation) return;

    const deviationValue = parseFloat(deviation) * (deviationDirection === 'W' ? -1 : 1);

    onSubmit({
      mode: 'standard',
      status: Math.abs(deviationValue) <= 2 ? 'ok' : 'error',
      heading: finalHeading,
      deviation: Math.abs(parseFloat(deviation)),
      deviationDirection,
      notes: notes.trim() || null,
      timestamp: new Date().toISOString()
    });
    onClose();
  };

  // ============================================================================
  // FULL MODE HANDLERS
  // ============================================================================

  const handleFullSubmit = () => {
    const finalHeading = getFinalHeading();
    if (finalHeading === null || isNaN(finalHeading) || !deviation || !method) return;

    const deviationValue = parseFloat(deviation) * (deviationDirection === 'W' ? -1 : 1);

    onSubmit({
      mode: 'full',
      status: Math.abs(deviationValue) <= 2 ? 'ok' : 'error',
      heading: finalHeading,
      deviation: Math.abs(parseFloat(deviation)),
      deviationDirection,
      method,
      gyroError: gyroError ? parseFloat(gyroError) : null,
      gyroHeading: gyroHeading ? parseFloat(gyroHeading) : null,
      notes: notes.trim() || null,
      timestamp: new Date().toISOString()
    });
    onClose();
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderHeadingSelector = () => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-3">
        Ship's Heading <span className="text-red-500">*</span>
      </label>

      {!showCustomHeading ? (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {HEADINGS.map(h => (
              <button
                key={h.value}
                onClick={() => setHeading(h.value)}
                className={`
                  min-h-[56px] rounded-lg border-2 transition-all
                  flex flex-col items-center justify-center
                  ${heading === h.value
                    ? 'bg-cyan-600 border-cyan-700 ring-4 ring-cyan-300 text-white'
                    : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                  }
                `}
              >
                <span className="text-lg font-bold">{h.label}</span>
                <span className="text-xs opacity-70">{h.value}°</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => { setShowCustomHeading(true); setHeading(null); }}
            className="w-full py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Enter custom heading
          </button>
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={customHeading}
              onChange={(e) => setCustomHeading(e.target.value)}
              placeholder="000"
              min="0"
              max="359"
              className="flex-1 px-4 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
              autoFocus
            />
            <span className="text-slate-500 font-medium">°</span>
          </div>
          <button
            onClick={() => { setShowCustomHeading(false); setCustomHeading(''); }}
            className="w-full flex items-center justify-center gap-1 py-2 text-sm text-slate-500"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to compass points
          </button>
        </div>
      )}
    </div>
  );

  const renderDeviationInput = () => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Observed Deviation <span className="text-red-500">*</span>
      </label>
      <div className="flex gap-2 items-center">
        <input
          type="number"
          value={deviation}
          onChange={(e) => setDeviation(e.target.value)}
          placeholder="0.0"
          min="0"
          max="30"
          step="0.5"
          className="flex-1 px-4 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
        />
        <span className="text-slate-500">°</span>
        <div className="flex gap-1">
          {['E', 'W'].map(dir => (
            <button
              key={dir}
              onClick={() => setDeviationDirection(dir)}
              className={`w-14 min-h-[56px] rounded-lg border-2 font-bold transition-all ${
                deviationDirection === dir
                  ? dir === 'E'
                    ? 'bg-green-600 border-green-700 text-white ring-4 ring-green-300'
                    : 'bg-red-600 border-red-700 text-white ring-4 ring-red-300'
                  : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {dir === 'E' ? '+E' : '-W'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER - QUICK MODE
  // ============================================================================

  const renderQuickMode = () => {
    if (step === 'initial') {
      return (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 text-center mb-6">
            Compare compass heading with GPS course
          </p>

          <button
            onClick={handleQuickOK}
            className="w-full min-h-[120px] bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors flex flex-col items-center justify-center gap-3 shadow-lg py-4"
          >
            <CheckCircle className="h-10 w-10" />
            <span className="text-xl font-semibold">Compass OK</span>
            <span className="text-sm text-green-100">No significant deviation</span>
          </button>

          <button
            onClick={handleQuickError}
            className="w-full min-h-[120px] bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors flex flex-col items-center justify-center gap-3 shadow-lg py-4"
          >
            <AlertTriangle className="h-10 w-10" />
            <span className="text-xl font-semibold">Deviation Found</span>
            <span className="text-sm text-amber-100">Record the deviation amount</span>
          </button>
        </div>
      );
    }

    // Quick error detail step
    return (
      <div className="space-y-5">
        <button
          onClick={() => setStep('initial')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            Select approximate deviation amount
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {DEVIATION_RANGES.map(range => (
            <button
              key={range.value}
              onClick={() => setDeviationRange(range.value)}
              className={`
                min-h-[72px] rounded-xl border-2 transition-all
                flex flex-col items-center justify-center
                ${deviationRange === range.value
                  ? 'bg-amber-600 border-amber-700 text-white ring-4 ring-amber-300'
                  : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                }
              `}
            >
              <span className="text-xl font-bold">{range.label}</span>
              <span className={`text-xs ${deviationRange === range.value ? 'text-amber-100' : 'text-slate-500'}`}>
                {range.description}
              </span>
            </button>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional observations..."
            rows={2}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg resize-none text-base"
          />
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER - STANDARD MODE
  // ============================================================================

  const renderStandardMode = () => (
    <div className="space-y-5">
      {renderHeadingSelector()}
      {renderDeviationInput()}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Reference object, conditions..."
          rows={2}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg resize-none text-base"
        />
      </div>
    </div>
  );

  // ============================================================================
  // RENDER - FULL MODE
  // ============================================================================

  const renderFullMode = () => (
    <div className="space-y-5">
      {renderHeadingSelector()}
      {renderDeviationInput()}

      {/* Method selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Check Method <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {CHECK_METHODS.map(m => (
            <button
              key={m.value}
              onClick={() => setMethod(m.value)}
              className={`
                min-h-[56px] px-3 rounded-lg border-2 transition-all text-sm font-medium
                ${method === m.value
                  ? 'bg-cyan-600 border-cyan-700 text-white ring-4 ring-cyan-300'
                  : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                }
              `}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Gyro comparison (optional) */}
      <div className="p-4 bg-slate-50 rounded-lg space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          Gyro Compass (if fitted)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Gyro Heading</label>
            <input
              type="number"
              value={gyroHeading}
              onChange={(e) => setGyroHeading(e.target.value)}
              placeholder="000.0"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-center"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Gyro Error</label>
            <input
              type="number"
              value={gyroError}
              onChange={(e) => setGyroError(e.target.value)}
              placeholder="0.0"
              step="0.1"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-center"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Reference object, conditions, comparison details..."
          rows={2}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg resize-none text-base"
        />
      </div>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  const getModeLabel = () => {
    switch (mode) {
      case 'quick': return 'Quick Check';
      case 'standard': return 'Standard Check';
      case 'full': return 'Full STCW Check';
      default: return 'Compass Check';
    }
  };

  const canSubmit = () => {
    if (mode === 'quick') {
      return step === 'initial' || (step === 'quick_error_detail' && deviationRange);
    }
    if (mode === 'standard') {
      const finalHeading = getFinalHeading();
      return finalHeading !== null && !isNaN(finalHeading) && deviation;
    }
    if (mode === 'full') {
      const finalHeading = getFinalHeading();
      return finalHeading !== null && !isNaN(finalHeading) && deviation && method;
    }
    return false;
  };

  const handleSubmit = () => {
    if (mode === 'quick' && step === 'quick_error_detail') {
      handleQuickErrorSubmit();
    } else if (mode === 'standard') {
      handleStandardSubmit();
    } else if (mode === 'full') {
      handleFullSubmit();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500 rounded-lg">
              <Compass className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{getModeLabel()}</h2>
              <p className="text-sm text-slate-300">WBC3 19.2.6 deviation record</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {mode === 'quick' && renderQuickMode()}
          {mode === 'standard' && renderStandardMode()}
          {mode === 'full' && renderFullMode()}
        </div>

        {/* Actions - Only show for non-initial quick mode steps and all other modes */}
        {(mode !== 'quick' || step !== 'initial') && (
          <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit()}
              className="flex-1 py-3 px-4 min-h-[56px] bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Log Check
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompassCheckModal;
